import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Package,
  Warehouse,
  TruckIcon,
  MapPin,
  AlertTriangle,
  CheckCircle2,
  User,
  Clock,
  FileText,
  Image as ImageIcon,
  Download,
  Filter } from
'lucide-react';
import { formatDateTime } from '@/shared/utils';

const eventIcons = {
  'Shipment Created': Package,
  'Received at Hub': Warehouse,
  'Temperature Sensor Paired': AlertTriangle,
  'Sorted for Route': Package,
  'Loaded on Vehicle': TruckIcon,
  'Departed Hub': TruckIcon,
  'Temperature Excursion Detected': AlertTriangle,
  'Driver Action Taken': User,
  'Out for Delivery': MapPin,
  'Delivered': CheckCircle2,
  'Failed Attempt': AlertTriangle
};

const actorTypeColors = {
  system: 'bg-gray-100 text-gray-700',
  warehouse_staff: 'bg-blue-100 text-blue-700',
  driver: 'bg-purple-100 text-purple-700',
  dispatcher: 'bg-indigo-100 text-indigo-700',
  customer: 'bg-green-100 text-green-700'
};

export const TrailsTimeline = ({ trail, compact = false }) => {const { t } = useTranslation("ezparcel");
  const [filter, setFilter] = useState('all');
  const [expandedEvents, setExpandedEvents] = useState([]);

  if (!trail || trail.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Package className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t("noTrailData", "No trail data available")}</p>
        </CardContent>
      </Card>);

  }

  const toggleExpand = (eventId) => {
    setExpandedEvents((prev) =>
    prev.includes(eventId) ?
    prev.filter((id) => id !== eventId) :
    [...prev, eventId]
    );
  };

  const filteredTrail = filter === 'all' ?
  trail :
  trail.filter((event) => event.actorType === filter);

  return (
    <Card className="card-elevated">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">{t("chainOfCustody", "Chain of Custody Trail")}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{t("completeAuditTrail", "Complete audit trail with")}
              {trail.length}{t("events", "events")}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />{t("filter", "Filter")}

            </Button>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />{t("exportTrail", "Export Trail")}

            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />

          {/* Events */}
          <div className="space-y-6">
            {filteredTrail.map((event, idx) => {
              const Icon = eventIcons[event.event] || Package;
              const isExpanded = expandedEvents.includes(event.id);
              const hasAttachments = event.attachments && event.attachments.length > 0;
              const isWarning = event.severity === 'warning' || event.severity === 'high';

              return (
                <div key={event.id} className="relative flex gap-4">
                  {/* Icon Circle */}
                  <div className={`relative z-10 flex h-12 w-12 items-center justify-center rounded-full border-2 ${
                  isWarning ?
                  'bg-red-100 border-red-500' :
                  'bg-card border-border'}`
                  }>
                    <Icon className={`h-5 w-5 ${isWarning ? 'text-red-600' : 'text-foreground'}`} />
                  </div>

                  {/* Event Content */}
                  <div className="flex-1 pb-6">
                    <div
                      className={`p-4 rounded-lg border ${
                      isWarning ? 'border-red-200 bg-red-50' : 'border-border bg-card'} hover:shadow-md transition-shadow cursor-pointer`
                      }
                      onClick={() => toggleExpand(event.id)}>
                      
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{event.event}</h4>
                            {event.severity &&
                            <Badge
                              variant="outline"
                              className={
                              event.severity === 'warning' || event.severity === 'high' ?
                              'border-red-500 text-red-700' :
                              'border-yellow-500 text-yellow-700'
                              }>
                              
                                {event.severity.toUpperCase()}
                              </Badge>
                            }
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {event.details}
                          </p>
                          
                          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDateTime(event.timestamp)}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {event.actor}
                            </span>
                            <Badge className={`text-xs ${actorTypeColors[event.actorType] || 'bg-gray-100'}`}>
                              {event.actorType.replace('_', ' ')}
                            </Badge>
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {event.location}
                            </span>
                            {hasAttachments &&
                            <span className="flex items-center gap-1 text-blue-600">
                                <FileText className="h-3 w-3" />
                                {event.attachments.length}{t("attachment", "attachment")}{event.attachments.length > 1 ? 's' : ''}
                              </span>
                            }
                          </div>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && event.metadata &&
                      <div className="mt-4 pt-4 border-t border-border">
                          <h5 className="text-sm font-medium mb-2">{t("eventMetadata", "Event Metadata")}</h5>
                          <div className="grid grid-cols-2 gap-3">
                            {Object.entries(event.metadata).map(([key, value]) =>
                          <div key={key} className="text-sm">
                                <span className="text-muted-foreground">{key}:</span>
                                <span className="ml-2 font-medium">{value}</span>
                              </div>
                          )}
                          </div>
                        </div>
                      }

                      {/* Attachments */}
                      {isExpanded && hasAttachments &&
                      <div className="mt-4 pt-4 border-t border-border">
                          <h5 className="text-sm font-medium mb-3">{t("attachments", "Attachments")}</h5>
                          <div className="grid grid-cols-2 gap-2">
                            {event.attachments.map((attachment, attachIdx) =>
                          <div
                            key={attachIdx}
                            className="flex items-center gap-2 p-2 border border-border rounded hover:bg-muted transition-colors cursor-pointer">
                            
                                {attachment.type === 'photo' ?
                            <ImageIcon className="h-4 w-4 text-muted-foreground" /> :

                            <FileText className="h-4 w-4 text-muted-foreground" />
                            }
                                <span className="text-sm flex-1 truncate">{attachment.name}</span>
                                <Download className="h-3 w-3 text-muted-foreground" />
                              </div>
                          )}
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                </div>);

            })}
          </div>
        </div>

        {/* Summary */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold">{trail.length}</p>
              <p className="text-xs text-muted-foreground">{t("totalEvents", "Total Events")}</p>
            </div>
            <div>
              <p className="text-2xl font-bold">
                {trail.filter((e) => e.actorType === 'driver').length}
              </p>
              <p className="text-xs text-muted-foreground">{t("driverActions", "Driver Actions")}</p>
            </div>
            <div>
              <p className="text-2xl font-bold">
                {trail.filter((e) => e.attachments && e.attachments.length > 0).length}
              </p>
              <p className="text-xs text-muted-foreground">{t("withEvidence", "With Evidence")}</p>
            </div>
            <div>
              <p className="text-2xl font-bold">
                {trail.filter((e) => e.severity).length}
              </p>
              <p className="text-xs text-muted-foreground">{t("alerts", "Alerts")}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>);

};